import React from "react";

import { Button } from "@replohq/lattice/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuSub,
  MenuSubContent,
  MenuSubTrigger,
  MenuTrigger,
} from "@replohq/lattice/components/menu/Menu";
import { Modal } from "@replohq/lattice/components/modal/Modal";
import { Tooltip } from "@replohq/lattice/components/tooltip/Tooltip";
import { useAdminPanelActions } from "@v2/features/admin/adminPanelStore";
import { shouldShowDebugOverlays } from "@v2/features/admin/debugPanelChrome";
import { useResetFiles } from "@v2/features/debug/useResetFiles";
import { NextDevServerContext } from "@v2/features/page-renderer/components/code-execution/context";
import { useDynamicCommandMenuItem } from "@v2/hooks/useDynamicCommandMenuItems";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import {
  getAirlockApiUrl,
  getBackendStagingAliasOverride,
  getBedrockApiUrl,
  getCoordinatorApiUrl,
  getPublisherUrl,
} from "@v2/utils/env";
import {
  Bug,
  Copy,
  ExternalLink,
  FileText,
  MessageSquare,
  RefreshCcw,
  ScrollText,
  Wrench,
  XOctagon,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import * as z from "zod";

type BackendRoutingRow = readonly [label: string, value: string];

const debugMenuTriggerButton = (
  <Button icon variant="tertiary" size="base">
    <Bug />
  </Button>
);
const copyIcon = <Copy />;
const externalLinkIcon = <ExternalLink />;
const fileTextIcon = <FileText />;
const messageSquareIcon = <MessageSquare />;
const refreshIcon = <RefreshCcw />;
const scrollTextIcon = <ScrollText />;
const wrenchIcon = <Wrench />;
const resetFilesIcon = <XOctagon className="text-rose" />;

export const DebugMenu = () => {
  const [isResetFilesConfirmModalOpen, setIsResetFilesConfirmModalOpen] =
    React.useState(false);
  const { open } = useAdminPanelActions();
  const [searchParams] = useSearchParams();

  const [showLogs, setShowLogs] = useLocalStorageState(
    "replo.debug.showNextDevServerLogs",
    false,
    { schema: z.boolean() },
  );

  const nextDevServerContext = React.useContext(NextDevServerContext);
  const sandboxUrl = nextDevServerContext?.url;

  const { resetFiles, isLoadingFilesReset } = useResetFiles();
  const shouldShowDebugOptions = shouldShowDebugOverlays();

  const chatId = searchParams.get("chatId");
  const fileId = searchParams.get("pageId");

  const isDebugMode = useIsDebugMode();
  const backendStagingAlias = getBackendStagingAliasOverride();
  const backendAliasLabel =
    backendStagingAlias === undefined
      ? "(default)"
      : (backendStagingAlias ?? "staging");
  const backendRoutingRows: BackendRoutingRow[] = [
    ["Publisher", getPublisherUrl()],
    ["Bedrock", getBedrockApiUrl()],
    ["Coordinator", getCoordinatorApiUrl()],
    ["Airlock", getAirlockApiUrl()],
    ["Backend alias", backendAliasLabel],
  ];
  const backendRoutingText = backendRoutingRows
    .map(([label, value]) => `${label}: ${value}`)
    .join("\n");

  // Open Chat in Admin (cmd-k menu item)
  const openChatInAdminItem = React.useMemo(() => {
    if (!isDebugMode || !chatId) {
      return null;
    }

    return {
      label: "Open Chat in Admin",
      description: `Open chat ${chatId} in Admin`,
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: ExternalLink },
      onSelect: () => {
        window.open(
          `https://admin.replo.app/dashboard/chats?chatId=${chatId}`,
          "_blank",
        );
      },
    };
  }, [isDebugMode, chatId]);
  useDynamicCommandMenuItem("debug-open-chat-admin", openChatInAdminItem);

  // Copy Chat ID (cmd-k menu item)
  const copyChatIdItem = React.useMemo(() => {
    if (!isDebugMode || !chatId) {
      return null;
    }

    return {
      label: "Copy Chat ID",
      description: `Copy ${chatId}`,
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: Copy },
      onSelect: () => {
        void navigator.clipboard.writeText(chatId);
      },
    };
  }, [isDebugMode, chatId]);
  useDynamicCommandMenuItem("debug-copy-chat-id", copyChatIdItem);

  // Open File in Admin (cmd-k menu item)
  const openFileInAdminItem = React.useMemo(() => {
    if (!isDebugMode || !fileId) {
      return null;
    }

    return {
      label: "Open File in Admin",
      description: `Open file ${fileId} in Admin`,
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: ExternalLink },
      onSelect: () => {
        window.open(
          `https://admin.replo.app/dashboard/files?fileId=${fileId}`,
          "_blank",
        );
      },
    };
  }, [isDebugMode, fileId]);
  useDynamicCommandMenuItem("debug-open-file-admin", openFileInAdminItem);

  // Copy File ID (cmd-k menu item)
  const copyFileIdItem = React.useMemo(() => {
    if (!isDebugMode || !fileId) {
      return null;
    }

    return {
      label: "Copy File ID",
      description: `Copy ${fileId}`,
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: Copy },
      onSelect: () => {
        void navigator.clipboard.writeText(fileId);
      },
    };
  }, [isDebugMode, fileId]);
  useDynamicCommandMenuItem("debug-copy-file-id", copyFileIdItem);

  // Copy Preview URL (cmd-k menu item)
  const copyPreviewUrlItem = React.useMemo(() => {
    if (!isDebugMode || !sandboxUrl) {
      return null;
    }

    return {
      label: "Copy Preview URL",
      description: `Copy ${sandboxUrl}`,
      group: "debug" as const,
      icon: { type: "iconComponent" as const, component: Copy },
      onSelect: () => {
        void navigator.clipboard.writeText(sandboxUrl);
      },
    };
  }, [isDebugMode, sandboxUrl]);
  useDynamicCommandMenuItem("debug-copy-preview-url", copyPreviewUrlItem);

  if (!shouldShowDebugOptions) {
    return null;
  }

  const copyToClipboard = (text: string) => {
    void navigator.clipboard.writeText(text);
  };

  return (
    <>
      <Menu>
        <Tooltip content="Debug Options">
          <MenuTrigger render={debugMenuTriggerButton} />
        </Tooltip>
        <MenuContent align="start" side="bottom" UNSAFE_className="w-[200px]">
          <MenuItem
            label="Open Debug Panel"
            startEnhancer={wrenchIcon}
            onClick={() => {
              open();
            }}
          />
          <MenuItem
            label="Refresh"
            startEnhancer={refreshIcon}
            onClick={() => {
              // reboot();
            }}
          />
          <MenuItem
            label={
              showLogs
                ? "Hide Next Dev Server Logs"
                : "Show Next Dev Server Logs"
            }
            startEnhancer={scrollTextIcon}
            onClick={() => {
              setShowLogs(!showLogs);
            }}
          />
          <MenuItem
            label="Copy Preview URL"
            startEnhancer={copyIcon}
            onClick={() => {
              copyToClipboard(sandboxUrl ?? "");
            }}
          />
          <MenuSub>
            <MenuSubTrigger
              label="Backend Routing"
              startEnhancer={wrenchIcon}
            />
            <MenuSubContent>
              <MenuItem
                label="Copy All"
                startEnhancer={copyIcon}
                onClick={() => {
                  copyToClipboard(backendRoutingText);
                }}
              />
              <MenuSeparator />
              {backendRoutingRows.map(([label, value]) => (
                <MenuItem
                  key={label}
                  label={label}
                  onClick={() => {
                    copyToClipboard(value);
                  }}
                />
              ))}
            </MenuSubContent>
          </MenuSub>
          {chatId && (
            <MenuSub>
              <MenuSubTrigger label="Chat" startEnhancer={messageSquareIcon} />
              <MenuSubContent>
                <MenuItem
                  label="Copy ID"
                  startEnhancer={copyIcon}
                  onClick={() => {
                    copyToClipboard(chatId);
                  }}
                />
                <MenuItem
                  label="View in Admin"
                  startEnhancer={externalLinkIcon}
                  onClick={() => {
                    window.open(
                      `https://admin.replo.app/dashboard/chats?chatId=${chatId}`,
                      "_blank",
                    );
                  }}
                />
              </MenuSubContent>
            </MenuSub>
          )}
          {fileId && (
            <MenuSub>
              <MenuSubTrigger label="File" startEnhancer={fileTextIcon} />
              <MenuSubContent>
                <MenuItem
                  label="Copy ID"
                  startEnhancer={copyIcon}
                  onClick={() => {
                    copyToClipboard(fileId);
                  }}
                />
                <MenuItem
                  label="View in Admin"
                  startEnhancer={externalLinkIcon}
                  onClick={() => {
                    window.open(
                      `https://admin.replo.app/dashboard/files?fileId=${fileId}`,
                      "_blank",
                    );
                  }}
                />
              </MenuSubContent>
            </MenuSub>
          )}
          <MenuSeparator />
          <MenuItem
            label="Reset Files"
            variant="destructive"
            startEnhancer={resetFilesIcon}
            onClick={() => {
              setIsResetFilesConfirmModalOpen(true);
            }}
          />
        </MenuContent>
      </Menu>
      <Modal
        open={isResetFilesConfirmModalOpen}
        onOpenChange={setIsResetFilesConfirmModalOpen}
        title="Reset Files"
        description="This action will reset all files in the project and remove all funnels. DATA WILL BE LOST. This is not a soft-delete which can be recovered, do not click this unless you really know what you're doing and are okay with losing existing pages and funnels in this project FOREVER."
        footer={
          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => setIsResetFilesConfirmModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void resetFiles()}
              loading={isLoadingFilesReset}
            >
              Reset Files
            </Button>
          </div>
        }
      />
    </>
  );
};
